import React from "react";
import { Link } from "react-router-dom";
import { Eye, Brain, Heart, Rocket, MapPin, Handshake, Mail, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";

const AboutPage = () => {
  return (
    <main data-testid="about-page">
      {/* HERO */}
      <section className="py-20 md:py-28 px-6" style={{ background: "var(--bg-soft)" }}>
        <div className="max-w-4xl mx-auto fade-up">
          <div className="flex items-center gap-2 mb-4">
            <span className="font-display text-3xl" style={{ color: "var(--sage-500)" }}>✦</span>
            <span className="section-eyebrow">Sobre nosotros</span>
          </div>
          <h1 className="section-title">
            Cuidamos tu visión <span className="handwritten">y lo que hay detrás</span>
          </h1>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-7 text-lg leading-relaxed max-w-3xl">
            En Salud y Visión llevamos <strong style={{ color: "var(--ink)" }}>más de 20 años</strong> acompañando a personas que sienten que su visión puede mejorar… aunque muchas veces no sepan exactamente cómo.
          </p>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-4 text-lg leading-relaxed max-w-3xl">
            Porque para nosotros, la visión no es solo ver nítido. Es <em style={{ color: "var(--sage-600)" }}>entender, procesar y relacionarse con el mundo de forma eficaz</em>.
          </p>
        </div>
      </section>

      {/* UNA FORMA DIFERENTE */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: "var(--sage-500)" }}>
              <Eye size={28} style={{ color: "var(--cream)" }} />
            </div>
            <span className="section-eyebrow">Nuestra filosofía</span>
            <h2 className="section-title mt-3" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)" }}>
              Una forma <span className="handwritten">diferente</span> de entender la visión
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="font-sans-body text-[color:var(--ink-soft)] leading-relaxed text-lg">
              A lo largo de nuestra experiencia hemos comprobado algo muy importante: <strong style={{ color: "var(--ink)" }}>muchas personas "ven bien"… pero no ven de forma eficiente</strong>.
            </p>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-4 leading-relaxed">Esto puede traducirse en:</p>
            <ul className="mt-4 space-y-3 font-sans-body">
              {["Dificultades en el aprendizaje", "Falta de concentración", "Fatiga visual", "Bajo rendimiento en el día a día"].map((x, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: "var(--sage-500)" }} />
                  <span className="text-[color:var(--ink)]">{x}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 p-5 rounded-2xl flex items-start gap-3" style={{ background: "var(--sage-50)", border: "1px solid var(--sage-100)" }}>
              <span className="font-display text-2xl leading-none shrink-0" style={{ color: "var(--sage-500)" }}>✦</span>
              <p className="font-sans-body text-[color:var(--ink)] leading-relaxed">
                Por eso nuestro enfoque va más allá de una simple graduación. Analizamos cómo funciona todo el sistema visual: <strong>ojos, cerebro y procesamiento de la información</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CAUSA NO SÍNTOMA */}
      <section className="py-20 px-6" style={{ background: "var(--sage-50)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: "var(--sage-500)" }}>
              <Brain size={28} style={{ color: "var(--cream)" }} />
            </div>
            <span className="section-eyebrow">Enfoque clínico</span>
            <h2 className="section-title mt-3" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)" }}>
              Especialistas en la <span className="handwritten">causa</span>, no solo en el síntoma
            </h2>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-6 text-lg leading-relaxed">
              Trabajamos desde la <strong style={{ color: "var(--ink)" }}>optometría comportamental</strong>, abordando la visión como un proceso neurológico complejo. Nuestro objetivo es identificar el origen del problema y ofrecer soluciones personalizadas a través de:
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4 mt-10">
            {[
              { title: "Exámenes visuales completos", desc: "Eficacia y procesamiento" },
              { title: "Terapia visual", desc: "Reeducación neurofuncional" },
              { title: "Control de miopía", desc: "Frenar la progresión" },
              { title: "Lentes especializadas", desc: "Esclerales, RPG, multifocales" },
              { title: "Ortoqueratología", desc: "Corrección nocturna" },
              { title: "WIVI · tecnología IA", desc: "Entrenamiento avanzado" },
              { title: "Audiología y audífonos", desc: "Audiometría y adaptación" },
              { title: "Terapia auditiva", desc: "Berard, Tomatis y SENA" },
            ].map((x, i) => (
              <div key={i} className="soft-card text-center" data-testid={`about-service-${i}`}>
                <div className="font-display text-3xl mb-3" style={{ color: "var(--sage-500)" }}>0{i + 1}</div>
                <h3 className="text-base font-medium" style={{ color: "var(--ink)" }}>{x.title}</h3>
                <p className="mt-2 font-sans-body text-xs text-[color:var(--ink-soft)]">{x.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 20 AÑOS ENFOQUE HUMANO */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-5" style={{ background: "var(--terracotta, #c98466)" }}>
              <Heart size={28} style={{ color: "var(--cream)" }} />
            </div>
            <span className="section-eyebrow">Enfoque humano</span>
            <h2 className="section-title mt-3" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)" }}>
              Más de <span className="handwritten">20 años</span> de experiencia con un enfoque humano
            </h2>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-6 text-lg leading-relaxed">
              Sabemos que detrás de cada dificultad visual hay algo más:
            </p>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
              {["Frustración en el colegio", "Inseguridad en el día a día", "Cansancio constante"].map((x, i) => (
                <div key={i} className="p-4 rounded-xl font-sans-body text-sm" style={{ background: "var(--cream)", border: "1px solid var(--sage-100)" }}>
                  <span className="font-display text-xl mr-2" style={{ color: "var(--terracotta, #c98466)" }}>·</span>{x}
                </div>
              ))}
            </div>
            <p className="font-sans-body text-[color:var(--ink-soft)] mt-8 leading-relaxed text-lg">
              Por eso no solo tratamos la visión. <strong style={{ color: "var(--ink)" }}>Acompañamos a la persona.</strong> Escuchamos, explicamos y guiamos en todo el proceso, adaptándonos a cada caso de forma individual.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              {[["20+", "años cuidando"], ["3500+", "pacientes"], ["100%", "personalizado"], ["2", "centros"]].map((x) => (
                <div key={x[0]} className="soft-card text-center" style={{ padding: "2rem 1rem" }}>
                  <div className="font-display text-5xl md:text-6xl" style={{ color: "var(--sage-500)" }}>{x[0]}</div>
                  <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)] mt-2 font-sans-body">{x[1]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INNOVACIÓN */}
      <section className="py-20 px-6" style={{ background: "var(--sage-500)", color: "var(--cream)" }}>
        <div className="max-w-5xl mx-auto grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-3 text-center">
            <div className="h-24 w-24 mx-auto rounded-2xl flex items-center justify-center" style={{ background: "var(--cream)" }}>
              <Rocket size={40} style={{ color: "var(--sage-600)" }} />
            </div>
          </div>
          <div className="md:col-span-9">
            <span className="section-eyebrow" style={{ color: "var(--sage-200)" }}>Tecnología</span>
            <h2 className="mt-3" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)", lineHeight: 1.15 }}>
              Innovación al <span className="handwritten" style={{ color: "var(--sage-200)" }}>servicio</span> de las personas
            </h2>
            <p className="font-sans-body mt-5 text-lg opacity-95 leading-relaxed">
              Combinamos nuestra experiencia clínica con herramientas innovadoras como el entrenamiento visual con tecnología digital, que nos permite <strong>evaluar y mejorar habilidades visuales de forma precisa y eficaz</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* SEGUIMOS CRECIENDO */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-sans-body text-sm font-semibold mb-6" style={{ background: "var(--terracotta, #c98466)", color: "var(--cream)" }}>
            <Sparkles size={14} /> Seguimos creciendo
          </div>
          <h2 className="section-title" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)" }}>
            Ampliamos para estar <span className="handwritten">más cerca</span> de ti
          </h2>
          <p className="font-sans-body text-[color:var(--ink-soft)] mt-6 text-lg leading-relaxed max-w-2xl mx-auto">
            Nuestro compromiso es llegar a más personas y ayudar cada vez mejor. Por eso <strong style={{ color: "var(--ink)" }}>ampliamos nuestro proyecto con un nuevo centro en Alcobendas</strong>, acercando nuestra forma de trabajar a más familias.
          </p>
          <Link to="/centros" className="btn-primary mt-8" data-testid="about-cta-centers">
            Ver nuestros centros <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* PROPÓSITO */}
      <section className="py-20 px-6" style={{ background: "var(--bg-soft)" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <div className="h-16 w-16 mx-auto rounded-2xl flex items-center justify-center mb-5" style={{ background: "var(--sage-500)" }}>
              <Handshake size={28} style={{ color: "var(--cream)" }} />
            </div>
            <span className="section-eyebrow">Nuestro propósito</span>
            <h2 className="section-title mt-3" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)" }}>
              Que cada persona <span className="handwritten">vea mejor</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-12">
            {[
              { t: "Vea mejor", d: "Nitidez, salud ocular, confort." },
              { t: "Comprenda mejor lo que ve", d: "Procesamiento eficiente, lectura, atención." },
              { t: "Rinda mejor en su vida diaria", d: "Colegio, trabajo, deporte, conducción." },
              { t: "Y se sienta más segura", d: "Sobre todo, más confianza en sí misma." },
            ].map((x, i) => (
              <div key={i} className="soft-card flex gap-4 items-start" data-testid={`purpose-${i}`}>
                <div className="font-display text-4xl shrink-0" style={{ color: "var(--sage-500)" }}>✦</div>
                <div>
                  <h3 className="text-lg font-medium" style={{ color: "var(--ink)" }}>{x.t}</h3>
                  <p className="mt-1 font-sans-body text-sm text-[color:var(--ink-soft)]">{x.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EQUIPO — placeholder */}
      <section className="py-20 px-6" id="equipo">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="section-eyebrow">Nuestro equipo</span>
            <h2 className="section-title mt-3" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)" }}>
              Las personas <span className="handwritten">detrás</span> de Salud y Visión
            </h2>
          </div>
          <div className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--sage-100) 0%, var(--sage-200) 100%)", border: "1px solid var(--sage-200)" }}>
            <div className="absolute top-6 left-6 font-display text-5xl opacity-50" style={{ color: "var(--sage-600)" }}>✦</div>
            <div className="absolute bottom-6 right-6 font-display text-4xl opacity-40" style={{ color: "var(--sage-600)" }}>✧</div>
            <div className="font-display text-6xl mb-6" style={{ color: "var(--sage-600)" }}>✦</div>
            <h3 className="font-display text-4xl md:text-5xl" style={{ color: "var(--sage-700)" }}>Muy pronto</h3>
            <p className="font-sans-body mt-4 text-[color:var(--sage-700)] opacity-80 max-w-lg mx-auto">
              Estamos preparando una foto del equipo al completo y una pequeña presentación de cada profesional. En unos días los conocerás.
            </p>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto rounded-[2rem] p-10 md:p-16 text-center relative overflow-hidden" style={{ background: "var(--sage-500)", color: "var(--cream)" }}>
          <div className="absolute top-8 right-8 font-display text-6xl opacity-20">✦</div>
          <div className="absolute bottom-8 left-8 font-display text-5xl opacity-20">✧</div>
          <Mail size={36} className="mx-auto mb-5" style={{ color: "var(--sage-200)" }} />
          <h2 style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)", lineHeight: 1.15 }}>
            Estamos aquí para <span className="handwritten" style={{ color: "var(--sage-200)" }}>ayudarte</span>
          </h2>
          <p className="font-sans-body mt-5 opacity-90 max-w-xl mx-auto text-lg">
            Si sientes que algo en tu visión o en la de tu hijo no termina de encajar, probablemente podamos ayudarte.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/servicios#autoevaluacion" className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-medium" style={{ background: "var(--cream)", color: "var(--sage-600)" }} data-testid="about-cta-autoeval">
              Haz tu autoevaluación <ArrowRight size={18} />
            </Link>
            <a href="/#contacto" className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-medium" style={{ border: "1.5px solid var(--cream)", color: "var(--cream)" }} data-testid="about-cta-contact">
              Escríbenos
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
